import { useMemo } from 'react';

import { ICause } from '@givebacks/api/core';
import { ClickableOptionsCard } from 'core/Components';
import { Card, Flex, Group, ImageMantine, Stack, Text } from 'ui';

import { useUserIntents } from '../../../../hooks/onboarding/useUserIntents';
import { CardRows } from './CardRows';

const LOGO_CONFIG = {
  BASE_SIZE: 48,
  SIZE_DECREMENT: 4,
  OVERLAP_MARGIN: -16,
} as const;

/**
 * Organizes the cause logos in a row with the first being the smallest and the last being the largest at 48px wide
 *
 * Handles reversing the list, and subtracting 4 pixels for each index
 *
 */
const CombinedCauseLogos = ({ logos }: { logos?: string[] }) => {
  if (!logos)
    return <ImageMantine h={LOGO_CONFIG.BASE_SIZE} w={LOGO_CONFIG.BASE_SIZE} radius={12} />;
  return (
    <Flex align="center" direction="row">
      {logos
        .slice(0, 3)
        .reverse()
        .map((logo, index, array) => {
          const isLast = index === array.length - 1;
          // Subtract 4 pixels for each index
          const size = LOGO_CONFIG.BASE_SIZE - index * LOGO_CONFIG.SIZE_DECREMENT;
          const marginLeft = LOGO_CONFIG.OVERLAP_MARGIN;
          return (
            <ImageMantine
              src={logo}
              h={size}
              w={size}
              radius={12}
              style={{ boxShadow: 'var(--mantine-shadow-md)' }}
              key={logo}
              ml={isLast ? 0 : marginLeft}
            />
          );
        })
        .reverse()}
    </Flex>
  );
};

const ConnectedCauseCard = ({ cause }: { cause: ICause }) => {
  const { userIntents, textData } = useUserIntents();

  const websiteLink = useMemo(() => cause?.links?.find((link) => link.type === 'website'), [cause]);
  return (
    <Card bd="1px solid gray.1" w="100%">
      <Card.Section bg="gray.0" p="sm">
        <Group gap="sm">
          <ImageMantine
            src={cause.logo?.url}
            h={LOGO_CONFIG.BASE_SIZE}
            w={LOGO_CONFIG.BASE_SIZE}
            radius={12}
            style={{ boxShadow: 'var(--mantine-shadow-md)' }}
          />
          <Stack gap="0">
            <Text fz="xs" fw={600}>
              {cause.name}
            </Text>
            <Text fz="xxs" fw={400}>
              {websiteLink?.url}
            </Text>
          </Stack>
        </Group>
      </Card.Section>
      <Card.Section>
        <ClickableOptionsCard bd="0">
          <CardRows cause={cause} intents={userIntents} textData={textData} />
        </ClickableOptionsCard>
      </Card.Section>
    </Card>
  );
};

const ConnectedCausesCombinedCard = ({ logos }: { logos?: string[] }) => {
  const { allCauseIntents, textData } = useUserIntents();

  if (!allCauseIntents || Object.values(allCauseIntents).every((value) => !value)) return null;

  return (
    <Card bd="1px solid gray.1" w="100%">
      <Card.Section bg="gray.0" p="sm">
        <Group gap="sm">
          <CombinedCauseLogos logos={logos} />
          <Stack gap="0">
            <Text fz="xs" fw={600}>
              All your orgs
            </Text>
          </Stack>
        </Group>
      </Card.Section>
      <Card.Section>
        <ClickableOptionsCard bd="0">
          <CardRows intents={allCauseIntents} textData={textData} />
        </ClickableOptionsCard>
      </Card.Section>
    </Card>
  );
};

export { ConnectedCauseCard, ConnectedCausesCombinedCard };
